import { AxiosError, AxiosResponse } from "axios";
import { Button, Form, Grid, Link, Message, Stack, Text } from "basis";
import { postConfirmSignUp, postResendOTP } from "library/api";
import { PostConfirmSignUp } from "library/api/users";
import {
  LoginUserParam,
  useAuthentication,
} from "library/hooks/auth/useAuthentication";
import { useState } from "react";
import { useEventTracking } from "react-event-tracker";
import { useMutation } from "react-query";
import { PostConfirmSignUpResponse } from "../../../library/api/users";
import { Loading } from "../../Loading/Loading";
import { OneTimeCode } from "../../OneTimeCode/OneTimeCode";
import { OTP_ERRORS } from "../../OtpInput/OtpInput";
import { ProgressBar } from "../../ProgressBar/ProgressBar";
import styles from "../registration.module.scss";

const initialValues = {
  oneTimeCode: "",
};

export const VerifyEmailPage = ({ params, setScreen }) => {
  const { trackEvent } = useEventTracking();
  const { steps } = params;
  const [state, setState] = useState({
    filled: false,
    otpErrorCode: "",
    errorMsg: "",
    currentScreen: "otp",
  });
  const { errorMsg, currentScreen } = state;
  const { loginUser } = useAuthentication();
  const updateState = (newState) => setState({ ...state, ...newState });

  const loginMutation = useMutation<void, AxiosError, LoginUserParam>(
    loginUser,
    { onError: () => updateState({ currentScreen: "unauthorized" }) },
  );
  const confirmSignUpMutation = useMutation<
    AxiosResponse<PostConfirmSignUpResponse>,
    AxiosError,
    PostConfirmSignUp
  >(postConfirmSignUp, {
    // if confirm sign up succeed, continue with sign in
    onSuccess: () => {
      // once confirm succeed, login user
      loginMutation.mutate({
        id: params.email,
        password: params.password,
      });
    },
    onError: (e) => {
      if (OTP_ERRORS[e.code]) {
        updateState({
          filled: false,
          otpErrorCode: e.code,
          errorMsg: OTP_ERRORS[e.code],
        });
      } else {
        // if other errors returned by api, just show the base api message
        updateState({ filled: false, errorMsg: e.message });
      }
    },
  });

  const onSubmit = async ({ errors, values }) => {
    if (Object.keys(errors).length === 0) {
      confirmSignUpMutation.mutate({
        id: params.email,
        passCode: values.oneTimeCode,
      });
    }
  };

  const loading = confirmSignUpMutation.isLoading || loginMutation.isLoading;
  const onResendCodeError = (e) => {
    if (OTP_ERRORS[e.code]) {
      updateState({
        otpErrorCode: e.code,
        errorMsg: OTP_ERRORS[e.code],
      });
    } else {
      updateState({ errorMsg: e.message });
    }
  };

  const otp = (
    <div>
      <ProgressBar steps={steps} currentStep={3} />
      {loading && (
        <div style={{ flex: "1 1", height: "428px" }}>
          <Loading />
        </div>
      )}
      {!loading && (
        <Stack gap="4" margin="8 0 0 0">
          <Text as="h3" textStyle="heading4">
            Verify your email
          </Text>

          {errorMsg && (
            <Message severity="blocking" bg="secondary.pink.t30">
              {errorMsg}
            </Message>
          )}
          <Form initialValues={initialValues} onSubmit={onSubmit}>
            <OneTimeCode
              codeLength={6}
              hasContactUsLink={false}
              otpRecipient={params.email}
              resendMutationFn={() => postResendOTP({ id: params.email })}
              onResendCodeError={onResendCodeError}
            />
            <Grid cols="2" cols-xs="160px 160px" colsGap="4" margin="12 0 0 0">
              <Grid.Item colSpan="0">
                <Button
                  type="submit"
                  width="160px"
                  onClick={() =>
                    trackEvent({
                      category: "application",
                      action: "application-verify",
                      location: "Verify your email",
                      label: "Verify",
                    })
                  }
                >
                  Verify
                </Button>
              </Grid.Item>
              <Grid.Item colSpan="1">
                <Button
                  width="160px"
                  variant="secondary"
                  onClick={() => {
                    setScreen("RegistrationDetails");
                  }}
                >
                  Back
                </Button>
              </Grid.Item>
            </Grid>
          </Form>
        </Stack>
      )}
    </div>
  );

  const unauthorized = (
    <Stack gap="4" margin="4 0 0 0">
      <Text as="h3" textStyle="heading4">
        Email address verified
      </Text>
      <Text>You are now required to log in again.</Text>
      <Link
        appearance="primary-button"
        width="160"
        href="/"
        onClick={() =>
          trackEvent({
            event: {
              category: "application",
              action: "application-login",
              location: "Email address verified",
              label: "Log in",
            },
          })
        }
        newTab={false}
      >
        Log in
      </Link>
    </Stack>
  );

  return (
    <div className={`${styles.wrapper} ${styles.verifyEmail}`}>
      <section>{currentScreen === "otp" ? otp : unauthorized}</section>
    </div>
  );
};
